<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDismissablePopover } from '@/composables/useDismissablePopover'
import type { GalleryStorageSummary } from '@/types/galleries'
import { formatBytes } from '@/utils/formatBytes'

const props = defineProps<{
  summary: GalleryStorageSummary
  compact?: boolean
}>()

const { t } = useI18n()

const triggerRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const popover = useDismissablePopover({
  idPrefix: 'gallery-storage',
  anchorRef: triggerRef,
  popoverRef: panelRef,
  closeOnEscape: true,
  returnFocusOnEscape: true,
  closeOnOutside: ['click'],
})

const percent = computed(() => Math.max(0, Math.min(100, props.summary.percent_used)))

const level = computed<'ok' | 'warning' | 'full'>(() => {
  if (props.summary.percent_used >= 100 || props.summary.uploads_blocked) return 'full'
  if (props.summary.percent_used >= 80) return 'warning'
  return 'ok'
})

const barClass = computed(() => {
  if (level.value === 'full') return 'bg-red-500'
  if (level.value === 'warning') return 'bg-amber-500'
  return 'bg-emerald-500'
})
</script>

<template>
  <div data-testid="gallery-storage-meter" :data-level="level">
    <div class="flex items-center gap-2">
      <p class="text-sm text-slate-300">
        {{ t('galleries.storage.used_of', { used: formatBytes(summary.used_bytes), quota: formatBytes(summary.quota_bytes) }) }}
      </p>
      <div class="relative">
        <button
          ref="triggerRef"
          type="button"
          class="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          :aria-label="t('galleries.storage.details')"
          :aria-expanded="popover.isOpen.value"
          :aria-controls="popover.id"
          @click="popover.toggle()"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
        </button>
        <div
          v-if="popover.isOpen.value"
          :id="popover.id"
          ref="panelRef"
          class="absolute left-0 top-full mt-2 z-20 w-72 rounded-xl bg-slate-800 border border-slate-700 shadow-xl p-4"
          role="region"
          :aria-label="t('galleries.storage.details')"
        >
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
            {{ t('galleries.storage.usage_by_event') }}
          </p>
          <ul v-if="summary.events.length > 0" class="space-y-1.5 mb-3">
            <li v-for="event in summary.events" :key="event.slug" class="flex items-center justify-between gap-3 text-sm">
              <span class="text-slate-200 truncate">{{ event.name }}</span>
              <span class="text-slate-400 shrink-0">{{ formatBytes(event.used_bytes) }}</span>
            </li>
          </ul>
          <p v-else class="text-sm text-slate-500 mb-3">{{ t('galleries.storage.no_usage') }}</p>
          <p class="text-xs text-slate-400">{{ t('galleries.storage.video_links_free') }}</p>
        </div>
      </div>
    </div>

    <div
      class="mt-2 h-1.5 w-full rounded-full bg-slate-700 overflow-hidden"
      role="progressbar"
      :aria-valuenow="Math.round(percent)"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="t('galleries.storage.meter_label')"
    >
      <div class="h-full rounded-full transition-all" :class="barClass" :style="{ width: `${percent}%` }" />
    </div>
    <p v-if="!compact" class="mt-1.5 text-xs text-slate-500">
      {{ t('galleries.storage.remaining', { remaining: formatBytes(summary.remaining_bytes) }) }}
    </p>
  </div>
</template>
